import { useEffect, useRef, useState } from 'react';

const isObject = value => value && typeof value === 'object' && !Array.isArray(value);

function readDraft(key, initial) {
  if (!key) return { value: initial, status: 'unavailable' };
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { value: initial, status: 'empty' };
    const saved = JSON.parse(raw);
    const form = saved?.formData;
    const valid = isObject(form)
      && Object.entries(initial.formData).every(([name, value]) => typeof value !== 'string' || typeof form[name] === 'string')
      && (form.otherSource == null || typeof form.otherSource === 'string')
      && (form.catalogueItemId == null || typeof form.catalogueItemId === 'string')
      && ['primaryContactId', 'organisationId'].every(name => form[name] === null || typeof form[name] === 'string')
      && Array.isArray(form.additionalContacts)
      && form.additionalContacts.every(person => isObject(person) && typeof person.contactId === 'string' && typeof person.role === 'string')
      && isObject(form.customFields)
      && Object.values(form.customFields).every(value => typeof value === 'string' || typeof value === 'number')
      && ['new', 'existing'].includes(saved.contactMode)
      && ['phoneNumber', 'email', 'socialHandle'].includes(saved.contactMethod);
    if (!valid) return { value: initial, status: 'invalid' };
    return { value: { formData: Object.fromEntries(Object.keys(initial.formData).map(name => [name, form[name] === undefined ? initial.formData[name] : form[name]])), contactMode: saved.contactMode, contactMethod: saved.contactMethod }, status: 'restored' };
  } catch (error) {
    return { value: initial, status: error instanceof SyntaxError ? 'invalid' : 'unavailable' };
  }
}

// The form is keyed by account, workspace and starting contact/organisation.
// Save at the edit event, rather than on unmount, so navigation cannot lose the last edit.
export default function useEnquiryDraft(key, initial) {
  const [state, setState] = useState(() => readDraft(key, initial));
  const current = useRef(state.value);
  const [hasEdits, setHasEdits] = useState(false);
  const update = patch => {
    const value = { ...current.current, ...patch };
    current.current = value;
    let status = 'saved';
    try {
      if (!key) throw new Error('No draft scope');
      localStorage.setItem(key, JSON.stringify(value));
    } catch (_) { status = 'unavailable'; }
    setHasEdits(true);
    setState({ value, status });
  };
  const clear = () => {
    let status = 'empty';
    try { if (key) localStorage.removeItem(key); } catch (_) { status = 'unavailable'; }
    current.current = initial;
    setHasEdits(false);
    setState({ value: initial, status });
  };
  useEffect(() => {
    if (state.status !== 'unavailable' || !hasEdits) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [state.status, hasEdits]);
  return { draft: state.value, status: state.status, update, clear };
}
